import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Input, Select, Typography, message } from 'antd'
import { SearchOutlined } from '@ant-design/icons';
import styled from 'styled-components'
import { debounce } from 'lodash'

import { Template } from "./components/Template"
import { BulkUpdateModal } from "./components/BulkUpdateModal"
import { VeritasSpinner } from "../../../../components/VeritasSpinner";

const { Text } = Typography

const PrescriptionTemplatesContainer = styled.div`
    height: 100%;
    min-height: 0;
    width: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
`

const ResultsScroll = styled.div`
    flex: 1 1 auto;
    min-height: 0;
    width: 100%;
    overflow-x: hidden;
    overflow-y: auto;
    -webkit-mask-image: linear-gradient(to bottom, transparent 0, #000 24px);
    mask-image: linear-gradient(to bottom, transparent 0, #000 24px);
`

const TemplatesContainer = styled.div`
    width: 100%;
    padding: 10px;
    display: flex;
    gap: 20px;
    flex-wrap: wrap;
`

const FilterPanel = styled.div`
    background: #f7f8fa;
    border: 1px solid #e8e8e8;
    border-radius: 8px;
    padding: 16px 20px;
    margin-bottom: 20px;
`

const FilterRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: 16px;
`

const FilterItem = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`

const NDC_LENGTH = 11

const TemplateSearch = ({
    templateFilters,
    setTemplateFilters,
    pharmacyOptions,
    categoryOptions,
    stateOptions,
}) => {
    // Local state makes text inputs respond on every keystroke.
    // The debounced function only updates the filter state, which drives actual filtering.
    const [searchValue, setSearchValue] = useState(templateFilters.search ?? '')
    const [medicationNameValue, setMedicationNameValue] = useState(templateFilters.medicationName ?? '')
    const [ndcValue, setNdcValue] = useState(templateFilters.ndc ?? '')

    const handleUpdateTemplateFilters = useMemo(
        () => debounce(update => {
            setTemplateFilters(prev => ({ ...prev, ...update }))
        }, 500),
        [setTemplateFilters]
    )

    useEffect(() => () => handleUpdateTemplateFilters.cancel(), [handleUpdateTemplateFilters])

    return (
        <FilterPanel>
            <FilterRow style={{ marginBottom: '14px' }}>
                <FilterItem>
                    <Text strong style={{ fontSize: '12px', color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Template Name</Text>
                    <Input
                        id="prescription-template-search-input"
                        type="text"
                        style={{ width: '300px' }}
                        placeholder="Search prescription template names.."
                        prefix={<SearchOutlined style={{ color: '#e2e7e9' }} />}
                        value={searchValue}
                        onChange={e => {
                            setSearchValue(e.target.value)
                            handleUpdateTemplateFilters({ search: e.target.value || null })
                        }}
                    />
                </FilterItem>
                <FilterItem>
                    <Text strong style={{ fontSize: '12px', color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Medication Name</Text>
                    <Input
                        id="prescription-template-medication-name-input"
                        type="text"
                        style={{ width: '300px' }}
                        placeholder="Min. 3 characters"
                        allowClear
                        value={medicationNameValue}
                        onChange={e => {
                            setMedicationNameValue(e.target.value)
                            handleUpdateTemplateFilters({ medicationName: e.target.value || null })
                        }}
                    />
                </FilterItem>
            </FilterRow>
            <FilterRow>
                <FilterItem>
                    <Text strong style={{ fontSize: '12px', color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Category</Text>
                    <Select
                        id="prescription-template-category-select"
                        allowClear
                        name="categoryIds"
                        style={{ width: '200px' }}
                        placeholder="Telemedicine Category"
                        onChange={val => handleUpdateTemplateFilters({ categoryIds: val })}
                    >
                    {categoryOptions.map(category => (
                        <Select.Option key={category._id} value={category._id}>{category.name}</Select.Option>
                    ))}
                    </Select>
                </FilterItem>
                <FilterItem>
                    <Text strong style={{ fontSize: '12px', color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Pharmacy</Text>
                    <Select
                        id="prescription-template-pharmacy-select"
                        allowClear
                        style={{ width: '200px' }}
                        placeholder="Filter by pharmacy"
                        onChange={val => setTemplateFilters(prev => ({ ...prev, pharmacyId: val ?? null }))}
                    >
                        {pharmacyOptions.map(pharmacy => (
                            <Select.Option key={pharmacy.id} value={pharmacy.id}>{pharmacy.name}</Select.Option>
                        ))}
                    </Select>
                </FilterItem>
                <FilterItem>
                    <Text strong style={{ fontSize: '12px', color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: '0.05em' }}>NDC</Text>
                    <Input
                        id="prescription-template-ndc-input"
                        type="text"
                        style={{ width: '150px' }}
                        placeholder={`${NDC_LENGTH}-digit NDC`}
                        maxLength={NDC_LENGTH}
                        allowClear
                        value={ndcValue}
                        onChange={e => {
                            setNdcValue(e.target.value)
                            handleUpdateTemplateFilters({ ndc: e.target.value || null })
                        }}
                    />
                </FilterItem>
                <FilterItem>
                    <Text strong style={{ fontSize: '12px', color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: '0.05em' }}>States</Text>
                    <Select
                        id="prescription-template-states-select"
                        mode="multiple"
                        allowClear
                        style={{ minWidth: '150px', maxWidth: '300px' }}
                        placeholder="Filter by state"
                        value={templateFilters.states}
                        onChange={val => setTemplateFilters(prev => ({ ...prev, states: val }))}
                    >
                        {stateOptions.map(abbr => (
                            <Select.Option key={abbr} value={abbr}>{abbr}</Select.Option>
                        ))}
                    </Select>
                </FilterItem>
                <FilterItem style={{ marginLeft: 'auto' }}>
                    <Text strong style={{ fontSize: '12px', color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Sort</Text>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <Select id="prescription-template-sort-by-select" style={{ width: '150px' }} value={templateFilters.sortBy} onChange={val => handleUpdateTemplateFilters({ sortBy: val })}>
                            <Select.Option value="title">Template Title</Select.Option>
                            <Select.Option value="medication.drugName">Medication Name</Select.Option>
                            <Select.Option value="createdAt">Created Date</Select.Option>
                            <Select.Option value="updatedAt">Last Updated</Select.Option>
                        </Select>
                        <Select id="prescription-template-sort-direction-select" style={{ width: '80px' }} value={templateFilters.sortDirection} onChange={val => handleUpdateTemplateFilters({ sortDirection: val })}>
                            <Select.Option value="asc">Asc</Select.Option>
                            <Select.Option value="desc">Desc</Select.Option>
                        </Select>
                    </div>
                </FilterItem>
            </FilterRow>
        </FilterPanel>
    )
}

export const PrescriptionTemplates = ({
    templates = [],
    selectTemplate,
    duplicateTemplate,
    deleteTemplate,
    bulkEditTemplates,
    templateFilters,
    setTemplateFilters,
    loading,
    pharmacyOptions = [],
    categoryOptions = [],
    stateOptions = [],
}) => {
    const [selectedIds, setSelectedIds] = useState([])
    const [bulkModalOpen, setBulkModalOpen] = useState(false)

    useEffect(() => {
        setSelectedIds([])
    }, [templateFilters])

    const toggleSelectTemplate = id => {
        setSelectedIds(prev =>
            prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
        )
    }

    const clearSelection = () => setSelectedIds([])

    const prescriptionTemplates = templates.filter(t => t.type === 'prescription')

    const allSelected = prescriptionTemplates.length > 0 && prescriptionTemplates.every(t => selectedIds.includes(t._id))

    const toggleSelectAll = () => {
        if (allSelected) {
            clearSelection()
        } else {
            setSelectedIds(prescriptionTemplates.map(t => t._id))
        }
    }

    const handleDeleteTemplate = useCallback(async templateId => {
        if (window.confirm("Are you sure you wish to delete this template?")) {
            try {
                await deleteTemplate(templateId)
            } catch (e) {
                message.error(e.message)
            }
        }
    }, [deleteTemplate])

    const handleBulkUpdate = async updates => {
        const success = await bulkEditTemplates(selectedIds, updates)
        if (success) setSelectedIds([])
        setBulkModalOpen(false)
    }

    return (
        <PrescriptionTemplatesContainer>
            <TemplateSearch
                templateFilters={templateFilters}
                setTemplateFilters={setTemplateFilters}
                pharmacyOptions={pharmacyOptions}
                categoryOptions={categoryOptions}
                stateOptions={stateOptions}
            />

            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px', padding: '10px 0' }}>
                <Button
                    id="prescription-template-select-all-btn"
                    onClick={toggleSelectAll}
                    disabled={prescriptionTemplates.length === 0}
                >
                    {allSelected ? 'Deselect All' : 'Select All'}
                </Button>
                {selectedIds.length > 0 && (
                    <>
                        <Button
                            id="prescription-template-bulk-update-btn"
                            type="primary"
                            style={{ background: '#7BAD7E', borderColor: '#7BAD7E' }}
                            onClick={() => setBulkModalOpen(true)}
                        >
                            Bulk Update ({selectedIds.length})
                        </Button>
                        <Button id="prescription-template-clear-selection-btn" onClick={clearSelection}>Clear Selection</Button>
                        <Typography.Text type="secondary">
                            {selectedIds.length} template{selectedIds.length !== 1 ? 's' : ''} selected
                        </Typography.Text>
                    </>
                )}
            </div>

            <ResultsScroll>
                {loading
                    ? (
                        <div style={{ height: '100%', width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <VeritasSpinner spinning />
                        </div>
                    )
                    : (
                        <TemplatesContainer>
                            {prescriptionTemplates.map(template => (
                                <Template
                                    key={template._id}
                                    template={template}
                                    selectTemplate={selectTemplate}
                                    duplicateTemplate={duplicateTemplate}
                                    handleDeleteTemplate={handleDeleteTemplate}
                                    isSelected={selectedIds.includes(template._id)}
                                    onToggleSelect={toggleSelectTemplate}
                                />
                            ))}
                        </TemplatesContainer>
                    )
                }
            </ResultsScroll>

            <BulkUpdateModal
                open={bulkModalOpen}
                selectedCount={selectedIds.length}
                onSubmit={handleBulkUpdate}
                onCancel={() => setBulkModalOpen(false)}
            />
        </PrescriptionTemplatesContainer>
    )
}

